import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Target, ShieldCheck } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { GoalCard } from "@/components/GoalCard";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EmptyGoals = () => (
  <div data-testid="goals-empty-state" className="rounded-3xl border-2 border-dashed border-amber-300 bg-white/70 p-8 sm:p-10 text-center">
    <div className="mx-auto mb-5 grid w-fit grid-cols-3 gap-2" aria-hidden="true">
      {[5, 10, 20, 50, 2, 100].map((v) => (
        <span key={v} className="grid h-12 w-12 place-items-center rounded-lg border-2 border-dashed border-amber-400 font-heading font-extrabold text-amber-800">{v}</span>
      ))}
    </div>
    <h3 className="font-heading text-2xl font-bold text-emerald-950">Seu primeiro cofrinho está esperando</h3>
    <p className="mx-auto mt-2 max-w-md text-lg text-slate-600">
      Você ainda não tem metas. Toque em “Criar minha meta” para começar.
    </p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState(null);
  const [goalsError, setGoalsError] = useState("");

  const loadGoals = useCallback(() => {
    setGoalsError("");
    setGoals(null);
    api.get("/goals")
      .then((response) => setGoals(response.data))
      .catch((requestError) => setGoalsError(formatApiError(requestError)));
  }, []);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const firstName = user.name.split(" ")[0];
  return (
    <div data-testid="dashboard-page" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <section className="fade-up">
          <p className="text-lg font-semibold text-amber-800">Que bom ver você por aqui</p>
          <h1 data-testid="dashboard-greeting" className="mt-1 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-emerald-950">
            Olá, {firstName}!
          </h1>
          <p className="mt-3 max-w-xl text-lg text-slate-700">Cada quadradinho marcado deixa você mais perto do seu sonho.</p>
        </section>

        <button
          onClick={() => navigate("/metas/nova")}
          data-testid="dashboard-create-goal-button"
          className="fade-up mt-10 flex w-full items-center gap-5 rounded-3xl bg-emerald-700 p-6 sm:p-8 text-left text-white shadow-[0_8px_0_0_#064e3b] transition-[transform,box-shadow,background-color] duration-150 hover:bg-emerald-800 active:translate-y-[6px] active:shadow-[0_2px_0_0_#064e3b]"
          style={{ animationDelay: "120ms" }}
        >
          <span className="grid h-16 w-16 sm:h-20 sm:w-20 shrink-0 place-items-center rounded-2xl bg-amber-300 text-emerald-950">
            <Plus className="h-10 w-10" strokeWidth={3} />
          </span>
          <span>
            <span className="block font-heading text-2xl sm:text-3xl font-extrabold">Criar minha meta</span>
            <span className="mt-1 block text-lg text-emerald-100">Escolha um sonho e quanto quer guardar</span>
          </span>
        </button>

        <section className="mt-14" data-testid="goals-section">
          <div className="mb-5 flex items-center gap-3">
            <Target className="h-7 w-7 text-emerald-700" />
            <h2 className="font-heading text-2xl font-bold text-emerald-950">Minhas metas</h2>
          </div>
          {goalsError ? (
            <div data-testid="goals-load-error" role="alert" className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-lg text-red-800">
              <p>Não foi possível carregar suas metas: {goalsError}</p>
              <button type="button" onClick={loadGoals} data-testid="goals-retry-button" className="mt-4 min-h-[48px] rounded-xl border-2 border-red-300 bg-white px-4 font-bold text-red-800 hover:bg-red-100">Tentar novamente</button>
            </div>
          ) : goals === null ? (
            <div className="h-40 animate-pulse rounded-3xl bg-white/60" />
          ) : goals.length === 0 ? <EmptyGoals /> : (
            <ul data-testid="goals-list" className="grid gap-4 sm:grid-cols-2">
              {goals.map((goal) => <GoalCard key={goal.id} goal={goal} />)}
            </ul>
          )}
        </section>

        <p className="mt-12 flex items-center gap-2 text-base text-slate-600">
          <ShieldCheck className="h-5 w-5 text-emerald-700" /> Só você vê as suas metas.
          {user.role === "admin" && (
            <Link to="/admin" data-testid="dashboard-admin-link" className="ml-auto font-bold text-emerald-800 underline">Painel do administrador</Link>
          )}
        </p>
      </main>
    </div>
  );
}
